import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Clock, Check } from 'lucide-react';
import { useAppointmentsInRange } from '@/hooks/useAppointments';
import { useClinicMembers } from '@/hooks/useClinicMembers';

interface AvailabilityPickerProps {
  clinicId: string;
  selectedClinicianId: string | null;
  selectedDateTime: Date | null;
  duration: number; // in minutes
  excludeAppointmentId?: string | null; // When editing, exclude current appointment
  onSelect: (dateTime: Date, clinicianId: string | null) => void;
  onClinicianChange?: (clinicianId: string | null) => void;
}

const CLINIC_START_HOUR = 7;
const CLINIC_END_HOUR = 21;
const SLOT_INTERVAL = 15; // minutes

function startOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(0, 0, 0, 0);
  return date;
}

function endOfDay(d: Date): Date {
  const date = new Date(d);
  date.setHours(23, 59, 59, 999);
  return date;
}

function addDays(d: Date, n: number): Date {
  const date = new Date(d);
  date.setDate(date.getDate() + n);
  return date;
}

function formatTime(date: Date): string {
  return date.toLocaleTimeString('da-DK', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(date: Date): string {
  return date.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });
}

export function AvailabilityPicker({
  clinicId,
  selectedClinicianId,
  selectedDateTime,
  duration,
  excludeAppointmentId,
  onSelect,
  onClinicianChange,
}: AvailabilityPickerProps) {
  const { t } = useTranslation();
  const [selectedDate, setSelectedDate] = useState<Date>(
    selectedDateTime ? startOfDay(selectedDateTime) : startOfDay(new Date())
  );
  const [viewStartDate, setViewStartDate] = useState<Date>(startOfDay(new Date()));

  const { data: members } = useClinicMembers(clinicId);

  // Generate week of dates for the view
  const weekDates = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => addDays(viewStartDate, i));
  }, [viewStartDate]);

  // Fetch appointments for the selected week
  const { data: appointments, isLoading } = useAppointmentsInRange(clinicId, {
    start: startOfDay(weekDates[0]).toISOString(),
    end: endOfDay(weekDates[6]).toISOString(),
    clinicianId: selectedClinicianId,
  });

  // Generate time slots for the selected date
  const timeSlots = useMemo(() => {
    const slots: Array<{ time: Date; available: boolean }> = [];
    const date = new Date(selectedDate);

    for (let hour = CLINIC_START_HOUR; hour < CLINIC_END_HOUR; hour++) {
      for (let minute = 0; minute < 60; minute += SLOT_INTERVAL) {
        const slotTime = new Date(date);
        slotTime.setHours(hour, minute, 0, 0);

        // Check if this slot conflicts with any appointment
        const slotEnd = new Date(slotTime.getTime() + duration * 60_000);
        
        const hasConflict = appointments?.some((apt) => {
          if (apt.id === excludeAppointmentId) return false; // Skip current appointment when editing
          
          const aptStart = new Date(apt.starts_at);
          const aptEnd = new Date(apt.ends_at);
          
          // Check for overlap
          return (
            (slotTime >= aptStart && slotTime < aptEnd) ||
            (slotEnd > aptStart && slotEnd <= aptEnd) ||
            (slotTime <= aptStart && slotEnd >= aptEnd)
          );
        });

        slots.push({
          time: slotTime,
          available: !hasConflict,
        });
      }
    }

    return slots;
  }, [selectedDate, appointments, duration, excludeAppointmentId]);

  const handlePreviousWeek = () => {
    setViewStartDate(addDays(viewStartDate, -7));
  };

  const handleNextWeek = () => {
    setViewStartDate(addDays(viewStartDate, 7));
  };

  const handleToday = () => {
    const today = startOfDay(new Date());
    setViewStartDate(today);
    setSelectedDate(today);
  };

  const isToday = (date: Date) => {
    const today = startOfDay(new Date());
    return date.getTime() === today.getTime();
  };

  const isSelected = (date: Date) => {
    return date.getTime() === selectedDate.getTime();
  };

  const isSelectedSlot = (slotTime: Date) => {
    if (!selectedDateTime) return false;
    return slotTime.getTime() === new Date(selectedDateTime).getTime();
  };

  return (
    <div className="space-y-4">
      {/* Employee Selector */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">
          {t('appointments.availability.select_clinician', 'Vælg behandler')}
        </label>
        <select
          value={selectedClinicianId ?? ''}
          onChange={(e) => onClinicianChange?.(e.target.value || null)}
          className="block w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900
            focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 
            dark:border-gray-600 dark:bg-gray-700 dark:text-white"
        >
          <option value="">{t('appointments.availability.any_clinician', 'Alle behandlere')}</option>
          {members?.map((m) => (
            <option key={m.user_id} value={m.user_id}>
              {m.name}
            </option>
          ))}
        </select>
      </div>

      {/* Week Navigation */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={handlePreviousWeek}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronLeft className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>

        <button
          type="button"
          onClick={handleToday}
          className="px-4 py-2 text-sm font-medium text-indigo-600 hover:text-indigo-700 dark:text-indigo-400"
        >
          {t('appointments.availability.today', 'I dag')}
        </button>

        <button
          type="button"
          onClick={handleNextWeek}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
        >
          <ChevronRight className="w-5 h-5 text-gray-600 dark:text-gray-300" />
        </button>
      </div>

      {/* Week View - Date Selector */}
      <div className="grid grid-cols-7 gap-2">
        {weekDates.map((date) => {
          const isPast = date < startOfDay(new Date());
          const selected = isSelected(date);
          const today = isToday(date);

          return (
            <button
              key={date.toISOString()}
              type="button"
              onClick={() => setSelectedDate(date)}
              disabled={isPast}
              className={`
                relative p-3 rounded-lg text-center transition-all
                ${selected
                  ? 'bg-indigo-600 text-white ring-2 ring-indigo-600 ring-offset-2'
                  : today
                  ? 'bg-indigo-50 text-indigo-600 dark:bg-indigo-900/20 dark:text-indigo-400'
                  : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-gray-50 dark:hover:bg-gray-700'
                }
                ${isPast ? 'opacity-40 cursor-not-allowed' : ''}
                border border-gray-200 dark:border-gray-700
              `}
            >
              <div className="text-xs font-medium">{formatDate(date).split(' ')[0]}</div>
              <div className="text-lg font-bold">{date.getDate()}</div>
            </button>
          );
        })}
      </div>

      {/* Time Slots */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
        <div className="flex items-center gap-2 mb-3">
          <Clock className="w-4 h-4 text-gray-500" />
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-200">
            {t('appointments.availability.available_times', 'Ledige tider')} - {formatDate(selectedDate)}
          </h4>
        </div>

        {isLoading ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            {t('appointments.availability.loading', 'Indlæser...')}
          </div>
        ) : (
          <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 max-h-[400px] overflow-y-auto">
            {timeSlots.map(({ time, available }) => {
              const selected = isSelectedSlot(time);
              const isPast = time < new Date();

              return (
                <button
                  key={time.toISOString()}
                  type="button"
                  onClick={() => onSelect(time, selectedClinicianId)}
                  disabled={!available || isPast}
                  className={`
                    relative px-3 py-2 rounded-lg text-sm font-medium transition-all
                    ${selected
                      ? 'bg-indigo-600 text-white ring-2 ring-indigo-600'
                      : available && !isPast
                      ? 'bg-green-50 text-green-700 hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30'
                      : 'bg-gray-100 text-gray-400 cursor-not-allowed dark:bg-gray-800 dark:text-gray-600'
                    }
                    border ${selected
                      ? 'border-indigo-600'
                      : available && !isPast
                      ? 'border-green-200 dark:border-green-800'
                      : 'border-gray-200 dark:border-gray-700'
                    }
                  `}
                >
                  {formatTime(time)}
                  {selected && (
                    <Check className="absolute top-1 right-1 w-3 h-3" />
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs text-gray-600 dark:text-gray-400 pt-2 border-t border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-green-100 dark:bg-green-900/20 border border-green-200 dark:border-green-800" />
          <span>{t('appointments.availability.available', 'Ledig')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-gray-100 dark:bg-gray-800 border border-gray-200 dark:border-gray-700" />
          <span>{t('appointments.availability.busy', 'Optaget')}</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-indigo-600 border border-indigo-600" />
          <span>{t('appointments.availability.selected', 'Valgt')}</span>
        </div>
      </div>
    </div>
  );
}
